/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  createContext,
  useState,
  useContext,
  useCallback,
  ReactNode,
} from "react";
import { getNewTokens } from "../service/reqUrl.service";
import { markActivity } from "./sessionActivity";
import { clearQueryCache } from "@/api/queryClient";
import { resetSessionErrorFlag } from "@/config/errorhandler";

interface AuthContextType {
  token: string | null;
  activeApp: string;
  user: {
    role?: any;
    full_name?: any;
    name: string;
    /** The tenant's readable name, from the login response's `CustomerName`.
     *  Absent for a session created before it was returned. */
    customerName?: string;
    roles: string[];
    RoleMapId?: number;
    isFinanceApprovalMandatory?: number;
  } | null;
  isTokenExpired(token: string): boolean | false;
  setAuth: (
    token: string,
    activeApp: string,
    userDetail: string,
    user: {
      role?: any;
      full_name?: any;
      name: string;
      customerName?: string;
      roles: string[];
      isFinanceApprovalMandatory?: number;
      RoleMapId: number;
    },
    refreshToken?: string
    // expiresIn: number
  ) => void;
  /** Silently exchange the stored refresh token for a fresh id_token.
   *  Returns true on success. Does NOT reset the idle clock. */
  refreshSession: () => Promise<boolean>;
  logout: () => void;
  saveDeviceId: (deviceId: string) => void;
  userDetail: string | null;
}

export const isTokenExpired = (token: string): boolean => {
  try {
    const [, payloadBase64] = token.split(".");
    const payload = JSON.parse(atob(payloadBase64));
    if (!payload.exp) return true;
    const expiry = payload.exp * 1000; // exp is in seconds
    return Date.now() > expiry;
  } catch {
    return true; // if token is invalid
  }
};

const AuthContext = createContext<AuthContextType>({
  token: null,
  user: null,
  activeApp: null,
  isTokenExpired,
  setAuth: async () => {},
  refreshSession: async () => false,
  logout: async () => {},
  saveDeviceId: async () => {},
  userDetail: null,
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [token, setToken] = useState<string | null>(
    localStorage.getItem("token") || null
  );
  const [activeApp, setActiveApp] = useState<string | null>(
    localStorage.getItem("activeApp") || null
  );
  const [userDetail, setUserDetail] = useState<string | null>(
    localStorage.getItem("userDetail") || null
  );
  const [user, setUser] = useState<{
    role?: any;
    full_name?: any;
    name: string;
    customerName?: string;
    roles: string[];
    RoleMapId?: number;
    isFinanceApprovalMandatory?: number;
  } | null>(JSON.parse(localStorage.getItem("user") || "null"));
  const [_, setTokenExp] = useState<number | null>(
    localStorage.getItem("tokenExpiration")
      ? JSON.parse(localStorage.getItem("tokenExpiration"))
      : null
  );

  const [deviceId, setDeviceId] = useState<string | null>(
    localStorage.getItem("deviceId") || null
  );

  const setAuth = (
    token: string,
    activeApp: string,
    userDetail: string,
    user: {
      role?: any;
      full_name?: any;
      name: string;
      customerName?: string;
      roles: string[];
      RoleMapId?: number;
      isFinanceApprovalMandatory?: number;
    },
    refreshToken?: string
    // expiresIn: number // Add the expiresIn field
  ) => {
    // const expirationTime = Date.now() + expiresIn * 1000;
    setToken(token);
    setUser(user);
    setUserDetail(userDetail);
    setActiveApp(activeApp);
    // setTokenExp(expirationTime);
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(user));
    // localStorage.setItem("tokenExpiration", expirationTime.toString());
    localStorage.setItem("userDetail", userDetail);
    localStorage.setItem("activeApp", activeApp);
    // Persist the Azure refresh token so the session can be renewed silently
    // while the user is active (see refreshSession / SessionManager).
    if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
    // Start the idle clock at login.
    markActivity();
  };

  // Exchange the stored refresh token for a new id_token. Intentionally does
  // not touch `lastActivity` — only real user interaction extends the session,
  // so an idle user is still logged out even though refreshes were succeeding.
  const refreshSession = useCallback(async (): Promise<boolean> => {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) return false;
    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_URL}${getNewTokens}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken }),
        }
      );
      const json = await res.json();
      const newIdToken = json?.data?.id_token;
      const newRefreshToken = json?.data?.refresh_token;
      if (json?.status === 200 && newIdToken) {
        setToken(newIdToken);
        localStorage.setItem("token", newIdToken);
        // Azure rotates the refresh token on each exchange — store the new one.
        if (newRefreshToken) {
          localStorage.setItem("refreshToken", newRefreshToken);
        }
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, []);

  const saveDeviceId = (deviceId: string) => {
    setDeviceId(deviceId);
    localStorage.setItem("deviceId", deviceId);
  };

  const logout = useCallback(() => {
    // Drop every cached response and cancel anything still in flight with the
    // now-dead token. `clear()`, not `invalidateQueries()` — invalidation only
    // marks entries stale and leaves the data in memory, where the next user to
    // log in on this device would be served it.
    clearQueryCache();
    resetSessionErrorFlag();

    setToken(null);
    setUser(null);
    setUserDetail(null);
    setActiveApp(null);
    setTokenExp(null);
    setDeviceId(null);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("tokenExpiration");
    localStorage.removeItem("deviceId");
    localStorage.removeItem("userDetail");
    localStorage.removeItem("activeApp");
    localStorage.removeItem("OrgDataSourceId");
    localStorage.removeItem("loggedEmail");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("lastActivity");
  }, []);

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        isTokenExpired,
        setAuth,
        refreshSession,
        logout,
        saveDeviceId,
        activeApp,
        userDetail,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  return useContext(AuthContext);
};
